import { computeConfidenceScore, type ConfidenceScore, type ScoringSettings } from './confidence-score'
import { resolveDomesticGSTRate } from './consolidator-fee'
import type { ConsolidatorFeeBreakdown } from './consolidator-fee'
import { departureTiming } from './departure-timing'
import { withConjunctionTicketKeys } from './conjunction-tickets'
import { isExchangedTicket, resolveExchange, withoutExchangedTickets } from './exchange'
import { roundCurrency } from './money'
import { computeRefundStatement, resolvePltFee, resolveSupplierFee } from './refund-statement'
import { assessRefundUncertainty, type RefundUncertainty } from './refund-uncertainty'
import type { RefundStatement } from './refund-statement'
import { surchargePolicyFor, type CarrierSurchargePolicy, type CarrierSurchargeTable } from './carrier-surcharges'
import { resolveAirlineDeductions } from './resolve-deductions'
import { resolveNettPaidToSupplier, resolvePaxGrossAllocation } from './resolve-statement-inputs'
import { searchOpinion } from './search-opinion'
import type { NettPaidResolution, PaxGrossAllocation } from './resolve-statement-inputs'
import type {
  AirlineDeductions,
  AirlineWaiverFee,
  BookingFinancialInputs,
  DepartureTiming,
  RefundQuoteData,
} from './types'
import { classifyTicketsByPaxType } from './utils'

/**
 * The one refund pipeline (SPEC.md): resolve the statement inputs — NettPaid, pax allocation,
 * airline deductions, fees — then run the two-ledger arithmetic. Serves the ops UI (with
 * operator overrides), the customer path, and fixtures alike.
 */

export interface StatementOverrides {
  /** The "airline cancellation fee" field: penalty + non-refundable taxes combined. */
  airlineDeductions?: number
  /** The "supplier cash refund" field, entered as the GROSS airline refund (decision A). */
  grossSupplierRefund?: number
  pltFee?: number
  adminFee?: number
  /** Booking-currency net fare corrections entered by ops. */
  netFarePerAdult?: number
  netFarePerChild?: number
}

export interface ComputeRefundStatementInput {
  refundQuoteData: RefundQuoteData
  booking: BookingFinancialInputs
  /** Defaults to every ticket on the booking. */
  selectedTicketNumbers?: Set<string>
  currency: string
  now?: Date
  overrides?: StatementOverrides
  /** The live scoring version from the Ops App; the package defaults when absent. */
  scoring?: ScoringSettings
  /** The Ops App's YQ/YR table; carriers not in it keep the estimation floor. */
  carrierSurcharges?: CarrierSurchargeTable
  /** A registered waiver the agent entered, every condition met: its fee replaces each ticket's own. */
  waiverFee?: AirlineWaiverFee
}

export interface RefundStatementResult {
  statement: RefundStatement
  deductions: AirlineDeductions
  nettPaid: NettPaidResolution
  paxGross: PaxGrossAllocation
  supplierFee: number
  /** Component split of supplierFee: flat per-pax fee, GST, commission claw-back. */
  supplierFeeBreakdown: ConsolidatorFeeBreakdown
  pltFee: number
  /**
   * NettPaid − deductions: what the airline returns before the consolidator takes its fee.
   * This is the figure ops overrides (decision A) and the PD note records alongside the net.
   */
  grossAirlineRefund: number
  /** The booking carrier's YQ/YR policy when the Ops table has it. */
  surchargePolicy?: CarrierSurchargePolicy
  isVoidWindow: boolean
  timing: DepartureTiming
  cancellingPax: number
  totalPax: number
  /** nettPaid + deduction warnings, merged — surface all of them in the UI. */
  warnings: string[]
  /**
   * How much to trust this statement's numbers without a manual check — PLT's own calibration of
   * the deduction resolution (source ladder position + every warning it raised), not a GDS/calculator
   * API's opinion of its own input. See confidence-score.ts.
   */
  confidence: ConfidenceScore
  /** Doubts measured in dollars, the range they span, and whether they matter (SPEC.md §10.8). */
  uncertainty: RefundUncertainty
  flags: {
    requiresManualDeductions: boolean
    isAllSelectedNonRefundable: boolean
    isTaxesOnlyRefund: boolean
    /** Rules-derived deductions (structured/parsed) — "verify with consolidator" territory. */
    isEstimate: boolean
  }
}

export function computeRefundQuoteStatement(input: ComputeRefundStatementInput): RefundStatementResult {
  const { booking, currency } = input
  const now = input.now ?? new Date()
  const overrides = input.overrides ?? {}
  const exchange = resolveExchange(input.refundQuoteData, booking)
  const rqd = withoutExchangedTickets(
    withConjunctionTicketKeys(applyNetFareOverrides(input.refundQuoteData, overrides)),
  )
  const tickets = rqd.tickets ?? []
  const selectedTicketNumbers = input.selectedTicketNumbers
    ? new Set([...input.selectedTicketNumbers].filter(n => !isExchangedTicket(rqd, n)))
    : new Set(tickets.map(t => t.number))

  const timing = departureTiming(booking.departureDate, now)
  const isVoidWindow = rqd.voidWindowDeadline != null && now < new Date(rqd.voidWindowDeadline)
  const noTicketsIssued = rqd.noTicketsIssued ?? false

  const adtCount = rqd.paxAdtCount ?? selectedTicketNumbers.size
  const chdCount = rqd.paxChdCount ?? 0
  const infCount = rqd.paxInfCount ?? (parseInt(booking.infantsTotal ?? '0') || 0)
  const nonInfants = (booking.travellersTotal ?? 0) - (parseInt(booking.infantsTotal ?? '0') || 0)
  const totalPax = nonInfants > 0 ? nonInfants : adtCount + chdCount || tickets.length
  const cancellingPax =
    selectedTicketNumbers.size > 0 ? selectedTicketNumbers.size : tickets.length === 0 ? adtCount + chdCount : 0

  const nettPaid = resolveNettPaidToSupplier({
    refundQuoteData: rqd,
    booking,
    selectedTicketNumbers,
    currency,
    exchange,
  })

  const surchargePolicy = surchargePolicyFor(input.carrierSurcharges, rqd)
  const resolvedDeductions = resolveAirlineDeductions({
    refundQuoteData: rqd,
    booking,
    selectedTicketNumbers,
    currency,
    now,
    isVoidWindow,
    noTicketsIssued,
    nettPaid,
    surchargePolicy,
    waiverFee: input.waiverFee,
    overrides: {
      deductionsTotal: overrides.airlineDeductions,
      grossSupplierRefund: overrides.grossSupplierRefund,
      adminFee: overrides.adminFee,
    },
  })
  const search = searchOpinion({
    searchCheck: rqd.searchCheck,
    deductions: resolvedDeductions,
    timing,
    cancellingPax,
    currency,
  })
  // On the deductions so the score sees them: a changed booking is not trusted at high yet, and a
  // search that disagrees is worth a look.
  const addedWarnings = [...exchange.warnings, ...(search?.verdict === 'differs' ? [search.warning] : [])]
  const deductions = addedWarnings.length
    ? { ...resolvedDeductions, warnings: [...resolvedDeductions.warnings, ...addedWarnings] }
    : resolvedDeductions

  const isFullSelection = selectedTicketNumbers.size === 0 || selectedTicketNumbers.size >= tickets.length
  const allTicketsNett = isFullSelection
    ? undefined
    : resolveNettPaidToSupplier({
        refundQuoteData: rqd,
        booking,
        selectedTicketNumbers: new Set(tickets.map(t => t.number)),
        currency,
        exchange,
      }).total

  const totalPaid = (booking.payment?.totalPaid ?? 0) + exchange.customerChangeFeeOutsidePlan
  const paxGross = resolvePaxGrossAllocation({
    totalPaid,
    isFullSelection,
    selectedNett: nettPaid.total,
    allTicketsNett,
    cancellingPax,
    totalPax,
  })

  const isAllSelectedNonRefundable =
    deductions.source === 'NON_REFUNDABLE_FARE' || deductions.source === 'AIRLINE_POLICY'
  const pltFee = resolvePltFee({
    cancellingPax,
    totalPax,
    salesPrice: booking.value,
    isVoidWindow,
    noTicketsIssued,
    isPartialNonRefundable: !isFullSelection && isAllSelectedNonRefundable,
    override: overrides.pltFee,
  })

  const paxTypes = classifyTicketsByPaxType(tickets, adtCount, chdCount)
  const selectedAdtChd = isFullSelection
    ? adtCount + chdCount || cancellingPax
    : [...selectedTicketNumbers].filter(n => paxTypes.adultNums.has(n) || paxTypes.childNums.has(n)).length
  const selectedInf = isFullSelection
    ? infCount
    : [...selectedTicketNumbers].filter(n => !paxTypes.adultNums.has(n) && !paxTypes.childNums.has(n)).length
  // The consolidator charges the flat refund fee per ticket that actually gets refunded; a $0-value
  // ticket (a lap infant on a domestic fare, etc.) has nothing to refund and incurs no fee — FLKBPV
  // (PLT2788078): fee on the 3 fare-bearing tickets, not the $0 infant. Only exclude tickets we can
  // prove are $0 (ticketPayments present with total ≤ 0); an absent entry leaves the fee unchanged.
  // Every consolidator rule charges the same per-pax rate across ADT/CHD/INF, so draining the infant
  // bucket first (the usual $0 case) keeps the total exact regardless of the classification split.
  const zeroValueCount = [...selectedTicketNumbers].filter(n => {
    const tp = rqd.ticketPayments?.[n]
    return tp != null && (tp.total ?? 0) <= 0
  }).length

  const feeInf = Math.max(0, selectedInf - zeroValueCount)
  const feeAdtChd = Math.max(0, selectedAdtChd - Math.max(0, zeroValueCount - selectedInf))
  // The consolidator only claws back issuance commission when the fare sale is reversed. A
  // taxes-only refund — a non-refundable fare, or an automated quote returning $0 of base fare —
  // leaves the whole fare with the airline, so PLT keeps the commission it earned and there is
  // nothing to claw back. KXCXAX (PLT2742634): airline-confirmed net cash-in 151.38 = gross 166.38
  // − flat 15, no clawback, despite a 16.47 commission on the (non-refundable) fare.
  const isTaxesOnlyRefund =
    isAllSelectedNonRefundable ||
    tickets.some(
      t =>
        selectedTicketNumbers.has(t.number) &&
        t.isRefundable === true &&
        t.refundTotals != null &&
        parseFloat(t.refundTotals.subtotal) === 0 &&
        parseFloat(t.refundTotals.taxes) > 0,
    )
  // A cancel penalty that meets or exceeds the base fare consumes the whole fare — none of it is
  // returned to the customer, so the sale effectively stands and the consolidator claws back no
  // commission (HIINNW/MU: AUD 300 penalty ≥ 207 base fare). This is a distinct clawback trigger
  // from a literal taxes-only refund; keep `isTaxesOnlyRefund` (a UI flag) unchanged.
  const selectedBaseFare = [...selectedTicketNumbers].reduce(
    (sum, n) => sum + (rqd.ticketPayments?.[n]?.subtotal ?? 0),
    0,
  )
  const fareNotReturned = isTaxesOnlyRefund || (selectedBaseFare > 0 && deductions.penalty >= selectedBaseFare)
  const commissionClawback = fareNotReturned
    ? 0
    : [...selectedTicketNumbers].reduce((sum, n) => sum + (rqd.ticketPayments?.[n]?.commission ?? 0), 0)

  const supplierFeeBreakdown = resolveSupplierFee({
    owningPcc: rqd.owningPcc,
    ticketingPcc: rqd.ticketingPcc,
    adtChdCount: feeAdtChd,
    infCount: feeInf,
    commissionClawback,
    gstRate: resolveDomesticGSTRate(rqd.fareRules),
    // Only what NettPaid actually counted can be kept back from it.
    processingFees: Math.min(rqd.untiedProcessingFees ?? 0, nettPaid.untiedFees ?? 0),
    isVoidWindow,
    noTicketsIssued,
  })
  const supplierFee = supplierFeeBreakdown.total

  const statement = computeRefundStatement({
    nettPaidToSupplier: nettPaid.total,
    totalPaidByPax: paxGross.totalPaidForSelection,
    deductions,
    supplierFee,
    pltFee,
  })

  const uncertainty = assessRefundUncertainty({
    statement,
    deductions,
    currency,
    spreadCap: input.scoring?.uncertaintySpreadCap,
  })

  return {
    statement,
    deductions,
    nettPaid,
    paxGross,
    supplierFee,
    supplierFeeBreakdown,
    pltFee,
    grossAirlineRefund: roundCurrency(nettPaid.total - deductions.total),
    surchargePolicy,
    isVoidWindow,
    timing,
    cancellingPax,
    totalPax,
    warnings: [...nettPaid.warnings, ...deductions.warnings],
    confidence: computeConfidenceScore(
      deductions,
      input.scoring,
      {
        basis: nettPaid.basis,
        warnings: nettPaid.warnings,
        noTicketsIssued,
      },
      uncertainty,
      search,
    ),
    uncertainty,
    flags: {
      requiresManualDeductions: deductions.requiresManualDeductions,
      isAllSelectedNonRefundable,
      isTaxesOnlyRefund,
      isEstimate: deductions.source === 'STRUCTURED_RULES' || deductions.source === 'PARSED_FARE_RULES',
    },
  }
}

function applyNetFareOverrides(rqd: RefundQuoteData, overrides: StatementOverrides): RefundQuoteData {
  if (overrides.netFarePerAdult === undefined && overrides.netFarePerChild === undefined) return rqd

  // Ops enter booking-currency figures; netFarePerAdt is stored in filing currency, so undo the
  // fare FX rate the resolvers will re-apply.
  const fx = rqd.fareToBookingCurrencyRate ?? 1
  return {
    ...rqd,
    netFarePerAdt: overrides.netFarePerAdult !== undefined ? overrides.netFarePerAdult / fx : rqd.netFarePerAdt,
    netFarePerChd: overrides.netFarePerChild !== undefined ? overrides.netFarePerChild / fx : rqd.netFarePerChd,
  }
}
